import { useLocalization } from 'localization'
import { message } from 'antd'
import { useMutation, useQueryClient } from 'react-query'
import {
	apiImport,
	createWallet,
	csvImport,
	disconnectAllIntegrations,
	disconnectIntegrationById,
	editWallet,
	merlinSyncIntegration,
	removeWallet,
	scanBlockchain,
	stopIntegration,
	vezgoIntegrate,
	vezgoSyncIntegration,
	wealthicaSyncIntegrations,
} from './services'
import queries from 'api/queries'
import { ResponseErrorType } from 'types/custom'

export const useAPIImport = () => {
	const { t } = useLocalization()
	const queryClient = useQueryClient()

	return useMutation(apiImport, {
		onSuccess: (_, { integration }) => {
			message.success(t('integrations.new.api.success', { integration }))
			queryClient.invalidateQueries([queries.integrations])
			queryClient.invalidateQueries([queries.featureAccess])
		},
		onError: (error: ResponseErrorType, { integration }) => {
			if (error?.response?.status !== 401) {
				message.error(t('integrations.new.api.error', { integration }))
			}
		},
	})
}

export const useCSVImport = () => {
	const { t } = useLocalization()
	const queryClient = useQueryClient()

	return useMutation(csvImport, {
		onSuccess: (_, { integration }) => {
			message.success(t('integrations.new.csv.success', { integration }))
			queryClient.invalidateQueries([queries.integrations])
			queryClient.invalidateQueries([queries.featureAccess])
		},
		onError: (error: ResponseErrorType, { integration }) => {
			if (error?.response?.status !== 401) {
				message.error(t('integrations.new.csv.error', { integration }))
			}
		},
	})
}

export const useAuthImport = () => {
	const { t } = useLocalization()
	const queryClient = useQueryClient()

	return useMutation(apiImport, {
		onSuccess: () => {
			message.success(t('integrations.new.auth.success'))
			queryClient.invalidateQueries([queries.integrations])
			queryClient.invalidateQueries([queries.featureAccess])
		},
		onError: (error: ResponseErrorType) => {
			if (error?.response?.status !== 401) {
				message.error(t('integrations.new.auth.error'))
			}
		},
	})
}

export const useScanBlockchain = () => {
	const { t } = useLocalization()
	const queryClient = useQueryClient()

	return useMutation(scanBlockchain, {
		onSuccess: () => {
			message.success(t('integrations.scan.success'))
			queryClient.invalidateQueries([queries.integrations])
			queryClient.invalidateQueries([queries.featureAccess])
		},
		onError: (error: ResponseErrorType) => {
			if (error?.response?.status !== 401) {
				message.error(t('integrations.scan.error'))
			}
		},
	})
}

export const useStopIntegration = () => {
	const { t } = useLocalization()
	const queryClient = useQueryClient()

	return useMutation(stopIntegration, {
		onSuccess: (_, id) => {
			message.success(t('integrations.stop.success'))
			queryClient.invalidateQueries([queries.integrations])
			queryClient.invalidateQueries([queries.integrationById, id])
			queryClient.invalidateQueries([queries.featureAccess])
		},
		onError: (error: ResponseErrorType) => {
			if (error?.response?.status !== 401) {
				message.error(t('integrations.stop.error'))
			}
		},
	})
}

export const useDisconnectIntegration = () => {
	const { t } = useLocalization()
	const queryClient = useQueryClient()

	return useMutation(disconnectIntegrationById, {
		onSuccess: (_, id) => {
			message.success(t('integrations.disconnect.success'))
			queryClient.invalidateQueries([queries.integrations])
			queryClient.invalidateQueries([queries.wallets])
			queryClient.invalidateQueries([queries.integrationById, id])
			queryClient.invalidateQueries([queries.featureAccess])
		},
		onError: (error: ResponseErrorType) => {
			if (error?.response?.status !== 401) {
				message.error(t('integrations.disconnect.error'))
			}
		},
	})
}

export const useDisconnectAllIntegrations = () => {
	const { t } = useLocalization()
	const queryClient = useQueryClient()

	return useMutation(disconnectAllIntegrations, {
		onSuccess: () => {
			message.success(t('integrations.disconnectAll.success'))
			queryClient.invalidateQueries([queries.integrations])
			queryClient.invalidateQueries([queries.wallets])
			queryClient.invalidateQueries([queries.featureAccess])
		},
		onError: (error: ResponseErrorType) => {
			if (error?.response?.status !== 401) {
				message.error(t('integrations.disconnectAll.error'))
			}
		},
	})
}

export const useRemoveWallet = () => {
	const { t } = useLocalization()
	const queryClient = useQueryClient()

	return useMutation(removeWallet, {
		onSuccess: () => {
			message.success(t('wallets.remove.success'))
			queryClient.invalidateQueries([queries.wallets])
			queryClient.invalidateQueries([queries.integrations])
			queryClient.invalidateQueries([queries.featureAccess])
		},
		onError: (error: ResponseErrorType) => {
			if (error?.response?.status !== 401) {
				message.error(t('wallets.remove.error'))
			}
		},
	})
}

export const useCreateWallet = () => {
	const { t } = useLocalization()
	const queryClient = useQueryClient()

	return useMutation(createWallet, {
		onSuccess: (_, { name }) => {
			message.success(t('wallets.add.success', { walletName: name }))
			queryClient.invalidateQueries([queries.wallets])
			queryClient.invalidateQueries([queries.integrations])
			queryClient.invalidateQueries([queries.featureAccess])
		},
		onError: (error: ResponseErrorType) => {
			if (error?.response?.status !== 401) {
				message.error(t('wallets.add.error'))
			}
		},
	})
}

export const useEditWallet = () => {
	const { t } = useLocalization()
	const queryClient = useQueryClient()

	return useMutation(editWallet, {
		onSuccess: (_, { name }) => {
			message.success(t('wallets.edit.success', { walletName: name }))
			queryClient.invalidateQueries([queries.wallets])
			queryClient.invalidateQueries([queries.walletById])
			queryClient.invalidateQueries([queries.integrations])
			queryClient.invalidateQueries([queries.featureAccess])
		},
		onError: (error: ResponseErrorType) => {
			if (error?.response?.status !== 401) {
				message.error(t('wallets.edit.error'))
			}
		},
	})
}

export const useVezgoIntegrate = () => {
	const { t } = useLocalization()
	const queryClient = useQueryClient()

	return useMutation(vezgoIntegrate, {
		onSuccess: (_, { source }) => {
			message.success(t('integrations.new.api.success', { integration: source }))
			queryClient.invalidateQueries([queries.integrations])
			queryClient.invalidateQueries([queries.featureAccess])
			queryClient.invalidateQueries([queries.wallets])
			queryClient.invalidateQueries([queries.walletById])
		},
		onError: (error: ResponseErrorType) => {
			if (error?.response?.status !== 401) {
				message.error(t('integrations.new.api.error'))
			}
		},
	})
}

export const useVezgoSyncIntegration = () => {
	const { t } = useLocalization()
	const queryClient = useQueryClient()

	return useMutation(vezgoSyncIntegration, {
		onSuccess: (_, { source }) => {
			message.success(t('integrations.sync.success', { integration: source }))
			queryClient.invalidateQueries([queries.integrations])
			queryClient.invalidateQueries([queries.featureAccess])
			queryClient.invalidateQueries([queries.wallets])
			queryClient.invalidateQueries([queries.walletById])
		},
		onError: (error: ResponseErrorType, { source }) => {
			if (error?.response?.status !== 401) {
				message.error(t('integrations.sync.error', { source }))
			}
		},
	})
}

export const useMerlinSyncIntegration = () => {
	const { t } = useLocalization()
	const queryClient = useQueryClient()

	return useMutation(merlinSyncIntegration, {
		onSuccess: () => {
			message.success(t('integrations.sync.success'))
			queryClient.invalidateQueries([queries.integrations])
			queryClient.invalidateQueries([queries.featureAccess])
			queryClient.invalidateQueries([queries.wallets])
			queryClient.invalidateQueries([queries.walletById])
		},
		onError: (error: ResponseErrorType) => {
			if (error?.response?.status !== 401) {
				message.error(t('integrations.sync.error'))
			}
		},
	})
}

export const useWealthicaSyncIntegrations = () => {
	const { t } = useLocalization()
	const queryClient = useQueryClient()

	return useMutation(wealthicaSyncIntegrations, {
		onSuccess: (_, { silent }) => {
			if (!silent) {
				message.success(t('integrations.wealthica.sync.success'))
			}
			queryClient.invalidateQueries([queries.integrations])
			queryClient.invalidateQueries([queries.featureAccess])
			queryClient.invalidateQueries([queries.wallets])
			queryClient.invalidateQueries([queries.walletById])
			queryClient.invalidateQueries([queries.current])
		},
		onError: (error: ResponseErrorType, { silent }) => {
			if (error?.response?.status !== 401 && !silent) {
				message.error(t('integrations.wealthica.sync.error'))
			}
		},
	})
}
